import React, { useState, useEffect, useRef, useMemo } from "react";
import ReactMapGL, {
  Popup,
  NavigationControl,
  FullscreenControl,
  Source,
  Layer,
} from "react-map-gl";
import { Chip } from "@material-ui/core";

const MAPBOX_TOKEN =
  "pk.eyJ1IjoieWFtYXNuYXgiLCJhIjoiY2prOTlub3cwMnQ5dzNsbWxpNXpxdTJoNyJ9.iPwzkcOPb0xHua5IwXCElA";

const COLOR_TAKEN = "#6e54a6";   // レビューOK（紫）
const COLOR_UNTAKEN = "#f53b78"; // 未撮影（ピンク）

const pctExpr = [
  "*",
  [
    "/",
    ["get", "taken_count"],
    ["+", ["get", "taken_count"], ["get", "untaken_count"]],
  ],
  100,
];

function PhotoShootMapNtt({ records = [], mapType, width, height }) {
  const mapRef = useRef(null);
  const popupHovered = useRef(false);

  const [mapStyle, setMapStyle] = useState(
    "mapbox://styles/yamasnax/clubgryio000s01p98ef99wcb"
  );
  const [terrainEnabled, setTerrainEnabled] = useState(false);
  const [hoveredFeature, setHoveredFeature] = useState(null);
  const [showClusters, setShowClusters] = useState(false);

  const markerColor = mapType === "taken" ? COLOR_TAKEN : COLOR_UNTAKEN;
  const isBoth = mapType === "taken+untaken";

  // --- ビューポート計算 ---
  const calculateBounds = (rows) => {
    const bounds = rows.reduce(
      (acc, curr) => {
        return {
          minLng: Math.min(acc.minLng, parseFloat(curr.longitude)),
          minLat: Math.min(acc.minLat, parseFloat(curr.latitude)),
          maxLng: Math.max(acc.maxLng, parseFloat(curr.longitude)),
          maxLat: Math.max(acc.maxLat, parseFloat(curr.latitude)),
        };
      },
      {
        minLng: Infinity,
        minLat: Infinity,
        maxLng: -Infinity,
        maxLat: -Infinity,
      }
    );

    const paddingPercentage = 0.1;
    const lngPadding = (bounds.maxLng - bounds.minLng) * paddingPercentage;
    const latPadding = (bounds.maxLat - bounds.minLat) * paddingPercentage;

    return {
      minLng: bounds.minLng - lngPadding,
      minLat: bounds.minLat - latPadding,
      maxLng: bounds.maxLng + lngPadding,
      maxLat: bounds.maxLat + latPadding,
    };
  };

  const calculateCenterAndZoom = (bounds) => {
    const center = [
      (bounds.minLng + bounds.maxLng) / 2,
      (bounds.minLat + bounds.maxLat) / 2,
    ];
    const zoom = Math.min(
      Math.log2(960 / (bounds.maxLng - bounds.minLng)),
      Math.log2(480 / (bounds.maxLat - bounds.minLat))
    );
    return { center, zoom };
  };

  const [viewport, setViewport] = useState({
    latitude: 38.5,
    longitude: 140.5,
    zoom: 6.2,
    bearing: 0,
    pitch: 0,
  });

  useEffect(() => {
    if (records.length > 0) {
      const bounds = calculateBounds(records);
      const { center, zoom } = calculateCenterAndZoom(bounds);
      setViewport({
        latitude: center[1],
        longitude: center[0],
        zoom,
        bearing: terrainEnabled ? 80 : 0,
        pitch: terrainEnabled ? 80 : 0,
      });
    } else {
      setViewport({
        latitude: 38.5,
        longitude: 140.5,
        zoom: 6.5,
        bearing: 0,
        pitch: 0,
      });
    }
  }, [records, terrainEnabled]);

  const toggleMapStyle = () => {
    setMapStyle((prev) =>
      prev === "mapbox://styles/yamasnax/clubgryio000s01p98ef99wcb"
        ? "mapbox://styles/mapbox/satellite-v9"
        : "mapbox://styles/yamasnax/clubgryio000s01p98ef99wcb"
    );
  };

  const toggleTerrain = () => {
    setTerrainEnabled((prev) => !prev);
  };

  // --- GeoJSON 変換 ---
  const toGeoJSON = (rows) => ({
    type: "FeatureCollection",
    features: (rows || []).map((r) => ({
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: [parseFloat(r.longitude), parseFloat(r.latitude)],
      },
      properties: {
        id: r.id,
        power_asset_id: r.power_asset_id,
        photography_target: r.photography_target,
        prefectural_area: r.prefectural_area,
        exchange_office_name: r.exchange_office_name,
        asset_info: r.asset_info,
        __kind: r.__kind,
      },
    })),
  });

  const takenRows = useMemo(
    () => (isBoth ? records.filter((r) => r.__kind === "taken") : records),
    [records, isBoth]
  );
  const untakenRows = useMemo(
    () => (isBoth ? records.filter((r) => r.__kind === "untaken") : []),
    [records, isBoth]
  );

  const gjTaken = useMemo(() => toGeoJSON(takenRows), [takenRows]);
  const gjUntaken = useMemo(() => toGeoJSON(untakenRows), [untakenRows]);

  const gjBoth = useMemo(
    () => ({
      type: "FeatureCollection",
      features: (records || []).map((r) => ({
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [parseFloat(r.longitude), parseFloat(r.latitude)],
        },
        properties: {
          id: r.id,
          power_asset_id: r.power_asset_id,
          photography_target: r.photography_target,
          prefectural_area: r.prefectural_area,
          exchange_office_name: r.exchange_office_name,
          asset_info: r.asset_info,
          __kind: r.__kind, // "taken" | "untaken"
        },
      })),
    }),
    [records]
  );

  // ホバー対象レイヤー
  const interactiveLayerIds = isBoth
    ? showClusters
      ? [
          "unclustered-points-both-taken",
          "unclustered-points-both-untaken",
        ] // クラスター円自体はホバーしない
      : [
          "unclustered-points-off-both-taken",
          "unclustered-points-off-both-untaken",
        ]
    : showClusters
    ? ["clusters", "unclustered-points"]
    : ["unclustered-points-off"];

  return (
    <div style={{ width, height }}>
      <ReactMapGL
        ref={mapRef}
        mapboxAccessToken={MAPBOX_TOKEN}
        style={{ width: "100%", height: "90%" }}
        mapStyle={mapStyle}
        {...viewport}
        onMove={(evt) => setViewport(evt.viewState)}
        onMouseMove={(event) => {
          if (popupHovered.current) return;
          const feature = event.features && event.features[0];
          setHoveredFeature(feature);
        }}
        interactiveLayerIds={interactiveLayerIds}
        terrain={
          terrainEnabled ? { source: "mapbox-dem", exaggeration: 1.5 } : null
        }
      >
        <FullscreenControl />
        <NavigationControl />

        {/* ====== 単独表示（taken または untaken） ====== */}
        {!isBoth && (
          <>
            <Source
              id="clustered-data"
              type="geojson"
              data={gjTaken}
              cluster={true}
              clusterMaxZoom={14}
              clusterRadius={50}
              generateId={true}
            >
              <Layer
                id="clusters"
                type="circle"
                source="clustered-data"
                filter={["has", "point_count"]}
                layout={{ visibility: showClusters ? "visible" : "none" }}
                paint={{
                  "circle-color": "#3E3E3E",
                  "circle-radius": [
                    "step",
                    ["get", "point_count"],
                    32,
                    100,
                    35,
                    750,
                    45,
                  ],
                  "circle-opacity": 0.9,
                }}
              />
              <Layer
                id="unclustered-points"
                type="circle"
                source="clustered-data"
                filter={["!", ["has", "point_count"]]}
                layout={{ visibility: showClusters ? "visible" : "none" }}
                paint={{
                  "circle-color": markerColor,
                  "circle-radius": 3,
                  "circle-stroke-width": 2,
                  "circle-stroke-color": [
                    "interpolate",
                    ["linear"],
                    pctExpr,
                    0,   "#f53b78",   // 0% → ピンク
                    100, "#6e54a6"    // 100% → 紫
                  ],
                }}
              />
              <Layer
                id="cluster-count-single"
                type="symbol"
                source="clustered-data"
                filter={["has", "point_count"]}
                layout={{
                  "text-field": [
                    "format",
                    ["get", "point_count"],
                    { "font-scale": 1.5 },
                    "\n",
                    {},
                    [
                      "concat",
                      [
                        "to-string",
                        [
                          "round",
                          [
                            "*",
                            [
                              "/",
                              ["get", "point_count"],
                              records.length || 1,
                            ],
                            100,
                          ],
                        ],
                      ],
                      "%",
                    ],
                    { "font-scale": 1.0 },
                  ],
                  "text-size": 12,
                  "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
                  "text-anchor": "center",
                  "text-allow-overlap": true,
                  "visibility": showClusters ? "visible" : "none",
                }}
                paint={{ "text-color": "#ffffff" }}
              />
            </Source>

            <Source
              id="unclustered-data"
              type="geojson"
              data={gjTaken}
              cluster={false}
              generateId={true}
            >
              <Layer
                id="unclustered-points-off"
                type="circle"
                source="unclustered-data"
                layout={{ visibility: showClusters ? "none" : "visible" }}
                paint={{
                  "circle-color": markerColor,
                  "circle-radius": 3,
                  "circle-stroke-width": 0,
                  "circle-stroke-color": "#ffffff",
                }}
              />
            </Source>
          </>
        )}

        {/* ====== 両方表示（taken+untaken） ====== */}
        {isBoth && (
          <>
            <Source
              id="clustered-both"
              type="geojson"
              data={gjBoth}
              cluster={true}
              clusterMaxZoom={14}
              clusterRadius={50}
              generateId={true}
              clusterProperties={{
                taken_count: [
                  "+",
                  ["case", ["==", ["get", "__kind"], "taken"], 1, 0],
                ],
                untaken_count: [
                  "+",
                  ["case", ["==", ["get", "__kind"], "untaken"], 1, 0],
                ],
              }}
            >
              {/* クラスタの白丸（ドーナツ風の土台） */}
              <Layer
                id="clusters-both-circle"
                type="circle"
                source="clustered-both"
                filter={["has", "point_count"]}
                layout={{ visibility: showClusters ? "visible" : "none" }}
                paint={{
                  // 中は白でドーナツっぽく
                  "circle-color": "#ffffff",
                  "circle-radius": [
                    "step",
                    ["+", ["get", "taken_count"], ["get", "untaken_count"]],
                    18,
                    100,
                    22,
                    1000,
                    28,
                    5000,
                    34,
                  ],
                  // ★ ここで縁の色を % に応じて変える
                  "circle-stroke-width": 3,
                  "circle-stroke-color": [
                    "case",
                    // 分母が 0 のとき（安全対策）
                    ["==", ["+", ["get", "taken_count"], ["get", "untaken_count"]], 0],
                    "#999999",

                    // 0〜50% → ピンク寄り
                    ["<", pctExpr, 50],
                    "#f53b78",

                    // 50〜80% → 中間色（薄い紫）
                    ["<", pctExpr, 80],
                    "#c06ad3",

                    // 80〜100% → 濃い紫（COLOR_TAKEN）
                    "#6e54a6",
                  ],
                  "circle-opacity": 0.9,
                }}
              />


              {/* 中央に「済XX%」表示 */}
              <Layer
                id="clusters-both-text"
                type="symbol"
                source="clustered-both"
                filter={["has", "point_count"]}
                layout={{
                  "text-field": [
                    "format",
                    [
                      "concat",
                      [
                        "to-string",
                        [
                          "round",
                          [
                            "*",
                            [
                              "/",
                              ["get", "taken_count"],
                              [
                                "+",
                                ["get", "taken_count"],
                                ["get", "untaken_count"],
                              ],
                            ],
                            100,
                          ],
                        ],
                      ],
                      "%",
                    ],
                    { "font-scale": 1.2 },
                  ],
                  "text-size": 12,
                  "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
                  "text-anchor": "center",
                  "text-offset": [0, -0.2],
                  "text-allow-overlap": true,
                  "visibility": showClusters ? "visible" : "none",
                }}
                paint={{ "text-color": "#333333" }}
              />

              {/* 100%以外のときだけ「未撮影本数」を%の下に表示 */}
              <Layer
                id="clusters-both-untaken-count"
                type="symbol"
                source="clustered-both"
                filter={[
                  "all",
                  ["has", "point_count"],
                  [">", ["get", "untaken_count"], 0], // ＝100%じゃない
                ]}
                layout={{
                  "text-field": [
                    "concat",
                    "未 ",
                    ["to-string", ["get", "untaken_count"]],
                  ],
                  "text-size": 10,
                  "text-font": ["Open Sans Regular", "Arial Unicode MS Regular"],
                  "text-anchor": "center",
                  "text-offset": [0, 1.25], // 下にずらす（ここで位置調整）
                  "text-allow-overlap": true,
                  "text-ignore-placement": true, // 密集時も優先的に出したい場合
                  "visibility": showClusters ? "visible" : "none",
                }}
                paint={{
                  "text-color": COLOR_UNTAKEN, // ピンク
                  "text-halo-color": "#ffffff",
                  "text-halo-width": 1.5,
                }}
              />

              {/* ズームインしてクラスターが崩れたときの個別点 */}
              <Layer
                id="unclustered-points-both-taken"
                type="circle"
                source="clustered-both"
                filter={[
                  "all",
                  ["!", ["has", "point_count"]],
                  ["==", ["get", "__kind"], "taken"],
                ]}
                layout={{ visibility: showClusters ? "visible" : "none" }}
                paint={{
                  "circle-color": COLOR_TAKEN,
                  "circle-radius": 3,
                  "circle-stroke-width": 2,
                  "circle-stroke-color": "#ffffff",
                }}
              />
              <Layer
                id="unclustered-points-both-untaken"
                type="circle"
                source="clustered-both"
                filter={[
                  "all",
                  ["!", ["has", "point_count"]],
                  ["==", ["get", "__kind"], "untaken"],
                ]}
                layout={{ visibility: showClusters ? "visible" : "none" }}
                paint={{
                  "circle-color": COLOR_UNTAKEN,
                  "circle-radius": 3,
                  "circle-stroke-width": 2,
                  "circle-stroke-color": "#ffffff",
                }}
              />
            </Source>

            <Source
              id="unclustered-both"
              type="geojson"
              data={gjBoth}
              cluster={false}
              generateId={true}
            >
              <Layer
                id="unclustered-points-off-both-taken"
                type="circle"
                source="unclustered-both"
                filter={["==", ["get", "__kind"], "taken"]}
                layout={{ visibility: showClusters ? "none" : "visible" }}
                paint={{
                  "circle-color": COLOR_TAKEN,
                  "circle-radius": 3,
                  "circle-stroke-width": 0,
                  "circle-stroke-color": "#ffffff",
                }}
              />
              <Layer
                id="unclustered-points-off-both-untaken"
                type="circle"
                source="unclustered-both"
                filter={["==", ["get", "__kind"], "untaken"]}
                layout={{ visibility: showClusters ? "none" : "visible" }}
                paint={{
                  "circle-color": COLOR_UNTAKEN,
                  "circle-radius": 3,
                  "circle-stroke-width": 0,
                  "circle-stroke-color": "#ffffff",
                }}
              />
            </Source>
          </>
        )}

        {/* ホバー時のツールチップ */}
        {hoveredFeature && (
          <Popup
            longitude={hoveredFeature.geometry.coordinates[0]}
            latitude={hoveredFeature.geometry.coordinates[1]}
            closeButton={false}
            closeOnClick={false}
            anchor="top"
            style={{
              backgroundColor: "white",
              padding: "0px",
              borderRadius: "8px",
              boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.2)",
            }}
            onMouseEnter={() => {
              popupHovered.current = true;
            }}
            onMouseLeave={() => {
              popupHovered.current = false;
              setTimeout(() => {
                if (!popupHovered.current) {
                  setHoveredFeature(null);
                }
              }, 300);
            }}
          >
            <div style={{ fontSize: "12px", fontWeight: "bold" }}>
              📍 {hoveredFeature.properties.prefectural_area}
            </div>
            <div>
              ID:{" "}
              {hoveredFeature.properties.id !== "null"
                ? hoveredFeature.properties.id
                : "--"}
            </div>
            <div>局名: {hoveredFeature.properties.exchange_office_name}</div>
            <div>電柱情報: {hoveredFeature.properties.asset_info}</div>
            <div>撮影対象: {hoveredFeature.properties.photography_target}</div>

            <button
              style={{
                marginTop: "10px",
                padding: "5px 10px",
                backgroundColor: "#4285F4",
                color: "white",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
              }}
              onClick={(e) => {
                e.stopPropagation();
                const lat = hoveredFeature.geometry.coordinates[1];
                const lng = hoveredFeature.geometry.coordinates[0];
                const url = `https://www.google.com/maps?q=&layer=c&cbll=${lat},${lng}`;
                window.open(url, "_blank");
              }}
              onMouseEnter={() => (popupHovered.current = true)}
              onMouseLeave={() => {
                popupHovered.current = false;
                setTimeout(() => {
                  if (!popupHovered.current) {
                    setHoveredFeature(null);
                  }
                }, 300);
              }}
            >
              🏠 Google Street View で見る
            </button>
          </Popup>
        )}

        {/* 操作用 Chip */}
        <div style={{ position: "absolute", top: 10, left: 10, zIndex: 1 }}>
          <Chip
            size="small"
            onClick={toggleMapStyle}
            label={
              mapStyle.includes("yamasnax")
                ? "航空写真表示にする"
                : "デフォルト表示にする"
            }
            style={{ backgroundColor: "#4caf50", color: "#fff" }}
          />
          <Chip
            size="small"
            onClick={toggleTerrain}
            label={terrainEnabled ? "3D表示" : "2D表示"}
            style={{ backgroundColor: "#4caf50", color: "#fff" }}
          />
          <Chip
            size="small"
            onClick={() => setShowClusters((prev) => !prev)}
            label={showClusters ? "クラスター表示" : "クラスター非表示"}
            style={{
              backgroundColor: showClusters ? "#4caf50" : "#7f898b",
              color: "#fff",
            }}
          />
        </div>

        {terrainEnabled && (
          <>
            <Source
              id="mapbox-dem"
              type="raster-dem"
              url="mapbox://mapbox.mapbox-terrain-dem-v1"
              tileSize={512}
              maxzoom={14}
            />
            <Layer
              id="terrain-data"
              type="hillshade"
              source="mapbox-dem"
              paint={{
                "hillshade-exaggeration": 0.5,
              }}
            />
          </>
        )}
      </ReactMapGL>
    </div>
  );
}

export default PhotoShootMapNtt;
